import React, { useCallback } from "react";
import { Formik, Form } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { Spinner } from "@chakra-ui/react";
import { ModalNew as Modal } from "components/Modal/ModalNew";
import { Button } from "newui/components/Button";
import { KeyPersonnelFullForm } from "newui/features/Company/KeyPersonnel/components/KeyPersonnelFullForm";
import { useCreateKeyPersonnel } from "newui/features/Company/KeyPersonnel/hooks/useKeyPersonnelMutations";
import { KEY_PERSONNEL_KEYS } from "newui/features/Company/KeyPersonnel/hooks/useKeyPersonnel";
import { createKeyPersonnelValidation } from "newui/features/Company/KeyPersonnel/validation/keyPersonnelValidation";
import { buildFormData } from "newui/features/Company/KeyPersonnel/key-personnel-api";
import type { KeyPersonnelFormValues } from "newui/features/Company/KeyPersonnel/types/KeyPersonnel.type";

interface AddNewKeyPersonnelModalProps {
	isOpen: boolean;
	onClose: () => void;
	onCreated: (id: number) => void;
}

const emptyInitialValues: KeyPersonnelFormValues = {
	first_name: "",
	last_name: "",
	email: "",
	position: "",
	experience: "",
	sort_order: null,
	sector_ids: [],
	disciplines: [{ expertise: "", expertiseId: null, disciplines: [] }],
	avatar_image: null,
	projects_completed: [],
	resume_files: [],
	is_resume_private: false,
};

const validationSchema = createKeyPersonnelValidation(2);

const AddNewKeyPersonnelModal: React.FC<AddNewKeyPersonnelModalProps> = ({
	isOpen,
	onClose,
	onCreated,
}) => {
	const queryClient = useQueryClient();
	const createMutation = useCreateKeyPersonnel();

	const handleSubmit = useCallback(
		async (values: KeyPersonnelFormValues) => {
			const fd = buildFormData(values);
			const result = await createMutation.mutateAsync(fd);
			const newId = (result as any)?.id;
			// Invalidate all key-personnel queries (list + paginated) so new person appears everywhere
			queryClient.invalidateQueries({ queryKey: KEY_PERSONNEL_KEYS.all });
			if (newId) {
				onCreated(newId);
			}
			onClose();
		},
		[createMutation, queryClient, onCreated, onClose],
	);

	if (!isOpen) {
		return null;
	}

	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			headerContent="Add New Key Personnel"
		>
			<Formik<KeyPersonnelFormValues>
				initialValues={emptyInitialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
			>
				{({ isValid, dirty }) => (
					<Form>
						<KeyPersonnelFullForm profileTypeId={2} />

						<div className="mt-6 flex justify-end gap-3 border-t border-gray-200 pt-4">
							<Button
								btntype="base"
								onClick={onClose}
								disabled={createMutation.isPending}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								btntype="primary"
								disabled={!dirty || !isValid || createMutation.isPending}
							>
								{createMutation.isPending ? (
									<Spinner size="sm" />
								) : (
									"Save"
								)}
							</Button>
						</div>
					</Form>
				)}
			</Formik>
		</Modal>
	);
};

export { AddNewKeyPersonnelModal };
